"use client";
import { AIRainbowWrapper } from "@cmp-src/tessa/AIRainbowWrapper";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  convertStringToContentBlocks,
  convertTessaTargetsToMentions
} from "@cmp-src/tessa/helpers";
import { cn } from "@gettactic/helpers";
import { useGlobalEscapeKey } from "@gettactic/helpers/src/hooks/useGlobalEscapeKey";
import { useLocalStorageHandler } from "@gettactic/helpers/src/storage/useLocalStorageHandler";
import type { FileUIPart } from "ai";
import { PanelLeftIcon, SquarePenIcon } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { AiSearchHeader } from "@/features/tessa/components/AiSearchHeader";
import { ChatBotBubbleBody } from "@/features/tessa/components/bubble/ChatBotBubbleBody";
import { ChatBotBubbleHeader } from "@/features/tessa/components/bubble/ChatBotBubbleHeader";
import { ChatButtonActions } from "@/features/tessa/components/bubble/ChatButtonActions";
import { ChatBotConversation } from "@/features/tessa/components/ChatBotConversation";
import { ChatBotPromptInput } from "@/features/tessa/components/ChatBotPromptInput";
import { ChatBotSidebar } from "@/features/tessa/components/ChatBotSidebar";
import { TessaFullScreenBackground } from "@/features/tessa/components/TessaFullScreenBackground";
import { useDraggableWindow } from "@/features/tessa/hooks/useDraggableWindow";
import { useTessaChat } from "@/features/tessa/hooks/useTessaChat";

const suggestions = [
  "Show my schedules",
  "Send a workplace request",
  "Where is my colleague today?"
];

const ChatComponent: React.FC<{
  selectedChatId: string | null | undefined;
  children: (
    chat: ReturnType<typeof useTessaChat>,
    isNewChat: boolean
  ) => React.ReactNode;
  isNewChat: boolean;
}> = ({ selectedChatId, children, isNewChat }) => {
  const chat = useTessaChat({
    selectedChatId: selectedChatId
  });
  return <>{children(chat, isNewChat)}</>;
};

export const AskTessaContent: React.FC = () => {
  const askTessa = useAskTessa();
  const selectedChatId = askTessa?.selectedChatId;
  const isNotFirstInteraction = !!selectedChatId && !askTessa?.isNewChat;
  const showSuggestions = true;
  const isPopup = askTessa?.isPopup?.state || false;
  const [popupSidebarOpen, setPopupSidebarOpen] = React.useState(false);

  const isSidebarOpen = askTessa?.sidebarDisclosure.isOpen ?? true;
  const isToolFullScreen = askTessa?.fullPageToolDisclosure.isOpen ?? false;

  useGlobalEscapeKey(() => {
    askTessa?.tessaDisclosure.close();
  });

  const handleSubmit =
    (chat: ReturnType<typeof useTessaChat>) =>
    (text: string, photos: FileUIPart[] = []) => {
      if (!text.trim() && photos.length === 0) {
        return;
      }
      if (!selectedChatId || selectedChatId === "new") {
        askTessa?.startNewChat(chat, text, photos);
        return;
      }
      chat.sendMessage({ text: text.trim(), files: photos });
    };

  const [windowRef, setWindowRef] = React.useState<HTMLDivElement | null>(null);
  const [handlerRef, setHandlerRef] = React.useState<HTMLDivElement | null>(
    null
  );
  const localStorage = useLocalStorageHandler("asktessa");
  const initialIsDocked = localStorage.get<boolean>("isDocked", false);
  const initialDockedSide = localStorage.get<"left" | "right" | null>(
    "dockedSide",
    null
  );

  useDraggableWindow(
    { current: windowRef },
    { current: handlerRef },
    {
      constrainToViewport: false,
      snap: {
        enabled: true,
        threshold: 56,
        anchorPreviewWidth: 60,
        dockedWidth: 420
      },
      onDockChange: (side) => {
        askTessa?.setDockedSide(side);
      },
      initialDocked:
        initialIsDocked && initialDockedSide ? initialDockedSide : undefined
    }
  );

  if (askTessa?.tessaDisclosure.isOpen !== true) return null;

  const handlePopupSidebarOpen = () => {
    setPopupSidebarOpen(true);
  };
  const handlePopupSidebarClose = () => {
    setPopupSidebarOpen(false);
  };

  return createPortal(
    <ChatComponent
      selectedChatId={selectedChatId}
      isNewChat={askTessa?.isNewChat ?? false}
    >
      {(chat, isNewChat) => {
        const hasActivity = chat.messages.length > 0 || chat.status !== "ready";
        const showConversation = isNotFirstInteraction || hasActivity;

        // Combine targets (as mentions) and initialPrompt (as text) for the initial value
        const targetsAsBlocks = convertTessaTargetsToMentions(
          askTessa?.tessaDisclosure?.data?.targets || []
        );
        const promptAsBlocks = convertStringToContentBlocks(
          askTessa?.tessaDisclosure?.data?.initialPrompt || ""
        );
        const initialValue = [...targetsAsBlocks, ...promptAsBlocks];

        return (
          <div
            ref={isPopup ? setWindowRef : undefined}
            key={isPopup ? "popup" : "bubble"}
            className={cn(
              "fixed transition-[height,width]",
              !askTessa?.dockedSide && isPopup
                ? "rounded-3xl border-1 border-neutral-200"
                : undefined,
              isPopup
                ? "z-modal h-[700px] max-h-screen w-[450px] max-w-screen overflow-hidden border bg-white shadow-2xl"
                : "top-0 left-[70px] h-screen w-[calc(100vw-70px)]"
            )}
            style={!isPopup ? {} : { bottom: 40, left: 40 }}
          >
            <TessaFullScreenBackground />
            {isPopup && (
              <div className={"absolute inset-0 flex flex-col"}>
                <ChatBotBubbleHeader
                  ref={setHandlerRef}
                  onPopupSidebarOpen={handlePopupSidebarOpen}
                  onPopupSidebarClose={handlePopupSidebarClose}
                />
                <ChatBotBubbleBody
                  popupSidebarOpen={popupSidebarOpen}
                  setPopupSidebarClosed={handlePopupSidebarClose}
                  chat={chat}
                />
              </div>
            )}
            {!isPopup && (
              <div className={cn(isPopup ? "" : "absolute inset-0 flex")}>
                <div
                  className={cn(
                    "relative z-modal w-[340px] transition-all hover:h-auto",
                    !isSidebarOpen
                      ? "w-px overflow-hidden opacity-0 transition-all"
                      : "border-r border-r-neutral-200 bg-[#F1F5F7]"
                  )}
                >
                  <div className={"absolute inset-0 mt-10 w-[340px]"}>
                    <ChatBotSidebar />
                  </div>
                </div>
                <div
                  className={
                    "fixed top-4 right-8 left-24 z-modal flex justify-between"
                  }
                >
                  <div className={"flex items-center gap-4"}>
                    <button
                      className={"relative"}
                      onClick={() => askTessa?.sidebarDisclosure?.toggle()}
                    >
                      <PanelLeftIcon />
                    </button>
                    <div className={"pt-0.5"}>
                      <button
                        className={cn(
                          "flex w-full items-center justify-center gap-2 rounded-4xl px-2 py-2 transition-opacity hover:bg-neutral-100"
                        )}
                        onClick={() => askTessa?.startNewChat()}
                      >
                        <SquarePenIcon /> New Chat
                      </button>
                    </div>
                  </div>
                  <ChatButtonActions />
                </div>
                <div
                  className={
                    "flex flex-1 flex-col items-center justify-center transition-all duration-300 ease-in-out"
                  }
                >
                  <div
                    className={cn(
                      "w-full transition-all",
                      showConversation && "flex-1"
                    )}
                  >
                    {showConversation && (
                      <ChatBotConversation
                        isNewChat={isNewChat}
                        key={chat.id || selectedChatId || "pending"}
                        chatId={chat.id || selectedChatId || ""}
                        liveMessages={chat.messages}
                        isThinking={chat.status !== "ready"}
                        className={cn(
                          "transition-[padding]",
                          "pr-4 md:pr-6",
                          !isSidebarOpen ? "pl-0" : "pl-[400px]",
                          isToolFullScreen
                            ? "pr-[calc(100vw-400px)] pl-4"
                            : undefined
                        )}
                      />
                    )}
                  </div>
                  <div
                    className={cn(
                      "w-full transition-all duration-300 ease-in-out",
                      showConversation && "mb-10"
                    )}
                  >
                    <div
                      className={cn(
                        "m-auto w-full max-w-[500px] transition-all duration-300 ease-in-out",
                        showConversation && "max-w-[800px]",
                        isToolFullScreen ? "m-0 max-w-[430px] px-3" : ""
                      )}
                    >
                      <div
                        className={cn(
                          "transition-all",
                          showConversation ? "opacity-0" : "py-2 opacity-100"
                        )}
                      >
                        <AiSearchHeader isVisible={!showConversation} />
                      </div>

                      <AIRainbowWrapper className={"z-modal mx-4 lg:mx-0"}>
                        <ChatBotPromptInput
                          handleSubmit={(chatText, aiText, photos) => {
                            handleSubmit(chat)(aiText, photos);
                          }}
                          inputSuggestions={askTessa?.suggestions}
                          initialValue={initialValue}
                          isCompact
                          showSuggestions
                        />
                      </AIRainbowWrapper>
                    </div>
                    <div
                      className={cn(
                        "flex w-full items-center justify-center overflow-hidden py-10 transition-all",
                        showConversation && "h-px py-0 opacity-0"
                      )}
                    >
                      <div className={"mx-2 flex gap-2 py-2"}>
                        {showSuggestions &&
                          suggestions.map((suggestion, suggestionIndex) => (
                            <button
                              className={
                                "z-modal rounded-4xl border-1 border-neutral-100 bg-white px-3 py-1.5 hover:cursor-pointer hover:bg-accent"
                              }
                              key={suggestionIndex}
                              onClick={() => {
                                handleSubmit(chat)(suggestion);
                              }}
                            >
                              {suggestion}
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </ChatComponent>,
    window.document.body
  );
};

export const AskTessa: React.FC = () => {
  return <AskTessaContent />;
};
